import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, apiUpload, getToken, urlImagem } from '../lib/api';
import { API_BASE } from '../config';
import Icone from '../components/Icones';
import AssinaturaPad from '../components/AssinaturaPad';

interface Unidade { id: number; nome: string; }
interface Ativo { id: number; nome: string; patrimonio?: string | null; modelo?: string | null; }
interface Plano {
  id: number;
  nome: string;
  unidade_id?: number | null;
  unidade_nome?: string | null;
  status: string;
  cliente_razao_social?: string | null;
  cliente_cnpj?: string | null;
  cliente_endereco?: string | null;
  ambientes_qtd?: number;
  ativos_qtd?: number;
  carga_total_btu?: string | number;
  responsavel_tecnico_nome?: string | null;
  responsavel_tecnico_conselho?: string | null;
  responsavel_tecnico_registro?: string | null;
  responsavel_tecnico_assinatura_url?: string | null;
  data_validade?: string | null;
}
interface Ambiente { id: number; plano_id: number; nome: string; ambiente_nome?: string; unidade_nome?: string | null; area_m2?: string | number | null; ocupantes_max?: number | null; atividade_ambiente?: string | null; }
interface PMOCAtivo { id: number; plano_id: number; ambiente_id?: number | null; ativo_id: number; ativo_nome: string; ambiente_nome?: string | null; tipo_climatizacao?: string | null; capacidade_btu?: string | number | null; capacidade_tr?: string | number | null; criticidade?: string | null; }
interface Atividade { id: number; plano_id: number; nome: string; periodicidade_dias: number; tolerancia_dias?: number; exige_medicao?: boolean | string; status?: string; }
interface Programacao { id: number; plano_id: number; pmoc_ativo_id: number; atividade_id: number; proxima_execucao: string; data_limite?: string | null; status_calculado?: string; atividade_nome?: string; ativo_nome?: string; ambiente_nome?: string | null; ultima_os_codigo?: string | null; }
interface Documento { id: number; plano_id?: number | null; tipo: string; titulo: string; data_validade?: string | null; arquivo_url: string; }
interface Indisponibilidade { id: number; ativo_nome: string; falha_inicio: string; mttr_operacional_minutos?: string | number | null; causa?: string | null; }
interface DashboardPMOC {
  totais: { planos: number; ativos_pmoc: number; programacoes: number; vencidas: number; a_vencer_15d: number; documentos_vencidos: number; carga_total_btu: number; carga_total_tr: number; };
  mttr_mtbf: { eventos_falha: number; mttr_operacional_hhmm: string; mtbf_hhmm: string; disponibilidade_percentual: number | null; };
  proximas: Programacao[];
}

const vazioPlano = { nome: '', unidade_id: '', cliente_razao_social: '', cliente_cnpj: '', cliente_endereco: '', responsavel_tecnico_nome: '', responsavel_tecnico_conselho: '', responsavel_tecnico_registro: '', responsavel_tecnico_assinatura_url: '', art_trt_numero: '', data_validade: '', status: 'ativo' };
const vazioAmbiente = { plano_id: '', nome: '', area_m2: '', ocupantes_max: '', atividade_ambiente: '', status: 'ativo' };
const vazioPMOCAtivo = { id: '', plano_id: '', ambiente_id: '', ativo_id: '', tipo_climatizacao: '', capacidade_btu: '', fluido_refrigerante: '', criticidade: 'media', status_pmoc: 'ativo' };
const vazioAtividade = { plano_id: '', nome: '', periodicidade_dias: '30', tolerancia_dias: '0', exige_foto: false, exige_observacao: false, exige_medicao: false, status: 'ativo' };
const vazioProgramacao = { plano_id: '', pmoc_ativo_id: '', atividade_id: '', data_inicio: '', proxima_execucao: '', status: 'ativo' };
const vazioDocumento = { plano_id: '', tipo: 'art_trt', titulo: '', data_emissao: '', data_validade: '', observacao: '' };
const vazioFalha = { ativo_id: '', ordem_servico_id: '', falha_inicio: '', reparo_inicio: '', reparo_fim: '', retorno_operacao: '', causa: '', observacao: '' };

function msgErro(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function fmtNumero(v: unknown): string {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n.toLocaleString('pt-BR') : '-';
}

function fmtData(v?: string | null): string {
  if (!v) return '-';
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [a, m, d] = v.split('-');
    return `${d}/${m}/${a}`;
  }
  return v;
}

function Stat({ icone, titulo, valor, detalhe, cor }: { icone: string; titulo: string; valor: string | number; detalhe?: string; cor: string }) {
  return (
    <div className="stat">
      <div className="stat-ic" style={{ background: cor + '1f', color: cor }}><Icone nome={icone} size={27} /></div>
      <div>
        <div className="stat-v" style={{ color: cor }}>{valor}</div>
        <div className="stat-l">{titulo}</div>
        {detalhe && <div style={{ color: 'var(--texto-suave)', fontSize: 13 }}>{detalhe}</div>}
      </div>
    </div>
  );
}

interface Fatia { rotulo: string; qtd: number; cor: string; }
function Barras({ titulo, fatias }: { titulo: string; fatias: Fatia[] }) {
  const max = Math.max(1, ...fatias.map((f) => f.qtd));
  return (
    <div className="card">
      <h3>{titulo}</h3>
      <div className="barras">
        {fatias.map((f) => (
          <div className="barra-item" key={f.rotulo}>
            <span className="barra-lbl">{f.rotulo}</span>
            <span className="barra-track"><span className="barra-fill" style={{ width: (f.qtd / max) * 100 + '%', background: f.cor }} /></span>
            <span className="barra-qtd">{f.qtd}</span>
          </div>
        ))}
        {!fatias.some((f) => f.qtd > 0) && <p className="vazio">Sem dados ainda</p>}
      </div>
    </div>
  );
}

export default function PMOC() {
  const params = useParams();
  const ativoParam = Number(params.ativoId || 0);
  const [aba, setAba] = useState('painel');
  const [dash, setDash] = useState<DashboardPMOC | null>(null);
  const [planos, setPlanos] = useState<Plano[]>([]);
  const [ambientes, setAmbientes] = useState<Ambiente[]>([]);
  const [pmocAtivos, setPmocAtivos] = useState<PMOCAtivo[]>([]);
  const [atividades, setAtividades] = useState<Atividade[]>([]);
  const [programacoes, setProgramacoes] = useState<Programacao[]>([]);
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [falhas, setFalhas] = useState<Indisponibilidade[]>([]);
  const [ativos, setAtivos] = useState<Ativo[]>([]);
  const [unidades, setUnidades] = useState<Unidade[]>([]);
  const [planoForm, setPlanoForm] = useState<Record<string, any>>(vazioPlano);
  const [ambienteForm, setAmbienteForm] = useState<Record<string, any>>(vazioAmbiente);
  const [ativoForm, setAtivoForm] = useState<Record<string, any>>({ ...vazioPMOCAtivo, ativo_id: ativoParam ? String(ativoParam) : '' });
  const [atividadeForm, setAtividadeForm] = useState<Record<string, any>>(vazioAtividade);
  const [programacaoForm, setProgramacaoForm] = useState<Record<string, any>>(vazioProgramacao);
  const [documentoForm, setDocumentoForm] = useState<Record<string, any>>(vazioDocumento);
  const [docArquivo, setDocArquivo] = useState<File | null>(null);
  const [falhaForm, setFalhaForm] = useState<Record<string, any>>(vazioFalha);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const planoSelecionado = Number(programacaoForm.plano_id || ativoForm.plano_id || ambienteForm.plano_id || atividadeForm.plano_id || documentoForm.plano_id || 0);
  const ambientesDoPlano = useMemo(() => planoSelecionado ? ambientes.filter((a) => Number(a.plano_id) === planoSelecionado) : ambientes, [ambientes, planoSelecionado]);
  const pmocAtivosDoPlano = useMemo(() => planoSelecionado ? pmocAtivos.filter((a) => Number(a.plano_id) === planoSelecionado) : pmocAtivos, [pmocAtivos, planoSelecionado]);
  const atividadesDoPlano = useMemo(() => planoSelecionado ? atividades.filter((a) => Number(a.plano_id) === planoSelecionado) : atividades, [atividades, planoSelecionado]);

  async function carregar() {
    const [d, p, amb, pa, atv, prog, docs, ind, ats, uns] = await Promise.all([
      api<DashboardPMOC>('/pmoc/dashboard.php'),
      api<Plano[]>('/pmoc/planos.php'),
      api<Ambiente[]>('/pmoc/ambientes.php'),
      api<PMOCAtivo[]>('/pmoc/ativos.php'),
      api<Atividade[]>('/pmoc/atividades.php'),
      api<Programacao[]>('/pmoc/programacoes.php'),
      api<Documento[]>('/pmoc/documentos.php'),
      api<Indisponibilidade[]>('/pmoc/indisponibilidades.php'),
      api<Ativo[]>('/ativos/ativos.php'),
      api<Unidade[]>('/cadastros/unidades.php?somente_ativos=1'),
    ]);
    setDash(d);
    setPlanos(p);
    setAmbientes(amb);
    setPmocAtivos(pa);
    setAtividades(atv);
    setProgramacoes(prog);
    setDocumentos(docs);
    setFalhas(ind);
    setAtivos(ats);
    setUnidades(uns);
  }

  useEffect(() => {
    carregar().catch((e) => setErro(msgErro(e)));
  }, []);

  async function salvarPlano(e: FormEvent) {
    e.preventDefault();
    setOcupado(true); setErro(''); setMsg('');
    try {
      const body = { ...planoForm, unidade_id: planoForm.unidade_id ? Number(planoForm.unidade_id) : null };
      await api('/pmoc/planos.php', { method: planoForm.id ? 'PUT' : 'POST', body });
      setPlanoForm(vazioPlano); setMsg('Plano PMOC salvo.'); await carregar();
    } catch (e2) { setErro(msgErro(e2)); } finally { setOcupado(false); }
  }

  async function salvarAssinaturaPlano(arquivo: File) {
    const planoId = Number(planoForm.id || 0);
    if (!planoId) {
      setErro('Salve o plano antes de incluir a assinatura do RT.');
      return;
    }
    setErro('');
    setMsg('');
    const fd = new FormData();
    fd.append('plano_id', String(planoId));
    fd.append('assinatura', arquivo);
    try {
      const res = await apiUpload<{ responsavel_tecnico_assinatura_url?: string }>('/pmoc/plano_assinatura.php', fd);
      setPlanoForm({ ...planoForm, responsavel_tecnico_assinatura_url: res.responsavel_tecnico_assinatura_url || planoForm.responsavel_tecnico_assinatura_url });
      setMsg('Assinatura do RT salva.');
      await carregar();
    } catch (e) {
      setErro(msgErro(e));
    }
  }

  async function salvarAmbiente(e: FormEvent) {
    e.preventDefault();
    setOcupado(true); setErro(''); setMsg('');
    try {
      const body = { ...ambienteForm, plano_id: Number(ambienteForm.plano_id), ocupantes_max: ambienteForm.ocupantes_max ? Number(ambienteForm.ocupantes_max) : null };
      await api('/pmoc/ambientes.php', { method: ambienteForm.id ? 'PUT' : 'POST', body });
      setAmbienteForm(vazioAmbiente); setMsg('Ambiente salvo.'); await carregar();
    } catch (e2) { setErro(msgErro(e2)); } finally { setOcupado(false); }
  }

  async function salvarAtivo(e: FormEvent) {
    e.preventDefault();
    setOcupado(true); setErro(''); setMsg('');
    try {
      const body = {
        ...ativoForm,
        plano_id: Number(ativoForm.plano_id),
        ativo_id: Number(ativoForm.ativo_id),
        ambiente_id: ativoForm.ambiente_id ? Number(ativoForm.ambiente_id) : null,
      };
      await api('/pmoc/ativos.php', { method: ativoForm.id ? 'PUT' : 'POST', body });
      setAtivoForm(vazioPMOCAtivo); setMsg('Equipamento PMOC salvo.'); await carregar();
    } catch (e2) { setErro(msgErro(e2)); } finally { setOcupado(false); }
  }

  async function salvarAtividade(e: FormEvent) {
    e.preventDefault();
    setOcupado(true); setErro(''); setMsg('');
    try {
      const body = { ...atividadeForm, plano_id: Number(atividadeForm.plano_id), periodicidade_dias: Number(atividadeForm.periodicidade_dias), tolerancia_dias: Number(atividadeForm.tolerancia_dias || 0) };
      await api('/pmoc/atividades.php', { method: atividadeForm.id ? 'PUT' : 'POST', body });
      setAtividadeForm(vazioAtividade); setMsg('Atividade PMOC salva.'); await carregar();
    } catch (e2) { setErro(msgErro(e2)); } finally { setOcupado(false); }
  }

  async function salvarProgramacao(e: FormEvent) {
    e.preventDefault();
    setOcupado(true); setErro(''); setMsg('');
    try {
      const body = { ...programacaoForm, plano_id: Number(programacaoForm.plano_id), pmoc_ativo_id: Number(programacaoForm.pmoc_ativo_id), atividade_id: Number(programacaoForm.atividade_id) };
      await api('/pmoc/programacoes.php', { method: programacaoForm.id ? 'PUT' : 'POST', body });
      setProgramacaoForm(vazioProgramacao); setMsg('Programação salva.'); await carregar();
    } catch (e2) { setErro(msgErro(e2)); } finally { setOcupado(false); }
  }

  async function salvarDocumento(e: FormEvent) {
    e.preventDefault();
    setOcupado(true); setErro(''); setMsg('');
    try {
      const fd = new FormData();
      Object.entries(documentoForm).forEach(([k, v]) => fd.append(k, String(v || '')));
      if (docArquivo) fd.append('arquivo', docArquivo);
      await apiUpload('/pmoc/documentos.php', fd);
      setDocumentoForm(vazioDocumento); setDocArquivo(null); setMsg('Documento salvo.'); await carregar();
    } catch (e2) { setErro(msgErro(e2)); } finally { setOcupado(false); }
  }

  async function salvarFalha(e: FormEvent) {
    e.preventDefault();
    setOcupado(true); setErro(''); setMsg('');
    try {
      const body = { ...falhaForm, ativo_id: Number(falhaForm.ativo_id), ordem_servico_id: falhaForm.ordem_servico_id ? Number(falhaForm.ordem_servico_id) : null };
      await api('/pmoc/indisponibilidades.php', { method: 'POST', body });
      setFalhaForm(vazioFalha); setMsg('Falha registrada.'); await carregar();
    } catch (e2) { setErro(msgErro(e2)); } finally { setOcupado(false); }
  }

  async function gerarOS(id: number) {
    setOcupado(true); setErro(''); setMsg('');
    try {
      await api('/pmoc/gerar_os.php', { method: 'POST', body: { programacao_id: id } });
      setMsg('OS PMOC gerada.'); await carregar();
    } catch (e) { setErro(msgErro(e)); } finally { setOcupado(false); }
  }

  async function abrirPdf() {
    const win = window.open('', '_blank');
    if (win) win.document.write('Gerando relatório PMOC...');
    try {
      const res = await fetch(`${API_BASE}/pmoc/relatorio_pdf.php`, { headers: { Authorization: 'Bearer ' + getToken() } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      if (win) win.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } catch {
      if (win) win.close();
      setErro('Não foi possível gerar o PDF PMOC.');
    }
  }

  return (
    <div className="pmoc-page">
      <div className="titulo-pg">
        <h1>PMOC</h1>
        {/* Permissao "CRIACAO DE OS SIMPLIFICADA": atalho para o fluxo que puxa
            ambientes e equipamentos do cadastro do cliente. */}
        {/* O PMOC pelo cliente agora nasce na ficha do cliente (Cadastros >
            Clientes), nos passos Cronograma e "Gerar e imprimir". A tela "Novo
            PMOC" separada deixou de existir, entao nao ha link para ela. */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn sec sm" onClick={() => carregar().catch((e) => setErro(msgErro(e)))}>Atualizar</button>
          <button className="btn sm" onClick={abrirPdf}>PDF PMOC</button>
        </div>
      </div>
      {erro && <div className="msg-erro">{erro}</div>}
      {msg && <div className="msg-ok">{msg}</div>}

      <div className="tabs" style={{ marginBottom: 16 }}>
        {['painel','planos','equipamentos','cronograma','documentos','mttr'].map((id) => <button key={id} className={aba === id ? 'on' : ''} onClick={() => setAba(id)}>{id === 'mttr' ? 'MTTR/MTBF' : id[0].toUpperCase() + id.slice(1)}</button>)}
      </div>

      {aba === 'painel' && dash && (
        <>
          <div className="grid cols4">
            <Stat icone="prancheta" titulo="Planos" valor={dash.totais.planos} detalhe="PMOC ativos e histórico" cor="#1e66f5" />
            <Stat icone="qr" titulo="Equipamentos PMOC" valor={dash.totais.ativos_pmoc} detalhe={`${fmtNumero(dash.totais.carga_total_btu)} BTU`} cor="#0f766e" />
            <Stat icone="calendario" titulo="A vencer" valor={dash.totais.a_vencer_15d} detalhe={`${dash.totais.vencidas} vencidas`} cor="#d97706" />
            <Stat icone="relogio" titulo="MTBF" valor={dash.mttr_mtbf.mtbf_hhmm || '-'} detalhe={`MTTR ${dash.mttr_mtbf.mttr_operacional_hhmm || '-'}`} cor="#7c3aed" />
          </div>
          <div style={{ marginTop: 16 }}>
            <Barras
              titulo="Situação das programações"
              fatias={[
                { rotulo: 'Vencidas', qtd: dash.totais.vencidas, cor: '#dc2626' },
                { rotulo: 'A vencer (15d)', qtd: dash.totais.a_vencer_15d, cor: '#d97706' },
                { rotulo: 'Em dia', qtd: Math.max(0, dash.totais.programacoes - dash.totais.vencidas - dash.totais.a_vencer_15d), cor: '#16a34a' },
              ]}
            />
          </div>
          <div className="grid cols2" style={{ marginTop: 16 }}>
            <div className="card">
              <h3>Próximas atividades</h3>
              <TabelaProgramacoes rows={dash.proximas || []} onGerar={gerarOS} ocupado={ocupado} />
            </div>
            <div className="card">
              <h3>Confiabilidade</h3>
              <div className="grid cols2">
                <Stat icone="raio" titulo="Falhas" valor={dash.mttr_mtbf.eventos_falha} cor="#dc2626" />
                <Stat icone="grafico" titulo="Disponibilidade" valor={dash.mttr_mtbf.disponibilidade_percentual !== null ? `${dash.mttr_mtbf.disponibilidade_percentual}%` : '-'} cor="#16a34a" />
              </div>
            </div>
          </div>
        </>
      )}

      {aba === 'planos' && (
        <div className="grid pmoc-grid-full">
          <form className="card" onSubmit={salvarPlano}>
            <h3>{planoForm.id ? 'Editar plano' : 'Novo plano PMOC'}</h3>
            <label>Nome *</label><input className="input" value={planoForm.nome || ''} onChange={(e) => setPlanoForm({ ...planoForm, nome: e.target.value })} required />
            <label>Unidade</label><select value={planoForm.unidade_id || ''} onChange={(e) => setPlanoForm({ ...planoForm, unidade_id: e.target.value })}><option value="">Geral</option>{unidades.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}</select>
            <h3>Cliente</h3>
            <label>Razão social</label><input className="input" value={planoForm.cliente_razao_social || ''} onChange={(e) => setPlanoForm({ ...planoForm, cliente_razao_social: e.target.value })} />
            <div className="grid cols2">
              <div><label>CNPJ</label><input className="input" value={planoForm.cliente_cnpj || ''} onChange={(e) => setPlanoForm({ ...planoForm, cliente_cnpj: e.target.value })} /></div>
              <div><label>Endereço</label><input className="input" value={planoForm.cliente_endereco || ''} onChange={(e) => setPlanoForm({ ...planoForm, cliente_endereco: e.target.value })} /></div>
            </div>
            <h3>Responsável técnico</h3>
            <label>Responsável técnico</label><input className="input" value={planoForm.responsavel_tecnico_nome || ''} onChange={(e) => setPlanoForm({ ...planoForm, responsavel_tecnico_nome: e.target.value })} />
            <div className="grid cols2">
              <div><label>Conselho</label><input className="input" value={planoForm.responsavel_tecnico_conselho || ''} onChange={(e) => setPlanoForm({ ...planoForm, responsavel_tecnico_conselho: e.target.value })} /></div>
              <div><label>Registro</label><input className="input" value={planoForm.responsavel_tecnico_registro || ''} onChange={(e) => setPlanoForm({ ...planoForm, responsavel_tecnico_registro: e.target.value })} /></div>
              <div><label>ART/TRT</label><input className="input" value={planoForm.art_trt_numero || ''} onChange={(e) => setPlanoForm({ ...planoForm, art_trt_numero: e.target.value })} /></div>
              <div><label>Validade</label><input className="input" type="date" value={planoForm.data_validade || ''} onChange={(e) => setPlanoForm({ ...planoForm, data_validade: e.target.value })} /></div>
            </div>
            {planoForm.id && (
              <AssinaturaPad
                titulo="Assinatura do RT"
                imagemUrl={urlImagem(planoForm.responsavel_tecnico_assinatura_url)}
                onSalvar={salvarAssinaturaPlano}
              />
            )}
            <div className="form-actions"><button className="btn" disabled={ocupado}>Salvar plano</button></div>
          </form>
          <div className="tabela-wrap">
            <table><thead><tr><th>Plano</th><th>Unidade</th><th>Equip.</th><th>Validade</th><th></th></tr></thead><tbody>
              {planos.map((p) => <tr key={p.id}><td><strong>{p.nome}</strong><br /><small>{p.cliente_razao_social || p.responsavel_tecnico_nome || '-'}</small></td><td>{p.unidade_nome || 'Geral'}</td><td>{p.ativos_qtd || 0}</td><td>{fmtData(p.data_validade)}</td><td className="acoes"><button className="btn sec sm" onClick={() => setPlanoForm(p)}>Editar</button></td></tr>)}
            </tbody></table>
          </div>
          <form className="card" onSubmit={salvarAmbiente}>
            <h3>Ambiente</h3>
            <SelectPlano value={ambienteForm.plano_id} planos={planos} onChange={(v) => setAmbienteForm({ ...ambienteForm, plano_id: v })} />
            <label>Nome *</label><input className="input" value={ambienteForm.nome || ''} onChange={(e) => setAmbienteForm({ ...ambienteForm, nome: e.target.value })} required />
            <div className="grid cols2">
              <div><label>Area m2</label><input className="input" value={ambienteForm.area_m2 || ''} onChange={(e) => setAmbienteForm({ ...ambienteForm, area_m2: e.target.value })} /></div>
              <div><label>Ocupantes</label><input className="input" type="number" value={ambienteForm.ocupantes_max || ''} onChange={(e) => setAmbienteForm({ ...ambienteForm, ocupantes_max: e.target.value })} /></div>
            </div>
            <label>Atividade do ambiente</label><input className="input" value={ambienteForm.atividade_ambiente || ''} onChange={(e) => setAmbienteForm({ ...ambienteForm, atividade_ambiente: e.target.value })} />
            <div className="form-actions"><button className="btn" disabled={ocupado}>Salvar ambiente</button></div>
          </form>
          <ListaSimples titulo="Ambientes" rows={ambientes.map((a) => [a.nome, planos.find((p) => p.id === a.plano_id)?.nome || '-', `${a.area_m2 || '-'} m2`])} />
        </div>
      )}

      {aba === 'equipamentos' && (
        <div className="grid pmoc-grid-full">
          <form className="card" onSubmit={salvarAtivo}>
            <h3>Equipamento PMOC</h3>
            <SelectPlano value={ativoForm.plano_id} planos={planos} onChange={(v) => setAtivoForm({ ...ativoForm, plano_id: v, ambiente_id: '' })} />
            <label>Equipamento *</label><select value={ativoForm.ativo_id || ''} onChange={(e) => setAtivoForm({ ...ativoForm, ativo_id: e.target.value })} required><option value="">Selecione</option>{ativos.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}</select>
            <label>Ambiente</label><select value={ativoForm.ambiente_id || ''} onChange={(e) => setAtivoForm({ ...ativoForm, ambiente_id: e.target.value })}><option value="">Sem ambiente</option>{ambientesDoPlano.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}</select>
            <div className="grid cols2">
              <div><label>Tipo</label><input className="input" value={ativoForm.tipo_climatizacao || ''} onChange={(e) => setAtivoForm({ ...ativoForm, tipo_climatizacao: e.target.value })} /></div>
              <div><label>BTU/h</label><input className="input" value={ativoForm.capacidade_btu || ''} onChange={(e) => setAtivoForm({ ...ativoForm, capacidade_btu: e.target.value })} /></div>
              <div><label>Fluido refrigerante</label><input className="input" value={ativoForm.fluido_refrigerante || ''} onChange={(e) => setAtivoForm({ ...ativoForm, fluido_refrigerante: e.target.value })} /></div>
              <div><label>Criticidade</label><select value={ativoForm.criticidade || 'media'} onChange={(e) => setAtivoForm({ ...ativoForm, criticidade: e.target.value })}><option value="baixa">Baixa</option><option value="media">Media</option><option value="alta">Alta</option><option value="critica">Critica</option></select></div>
            </div>
            <div className="form-actions"><button className="btn" disabled={ocupado}>Salvar equipamento PMOC</button></div>
          </form>
          <div className="tabela-wrap">
            <table><thead><tr><th>Equipamento</th><th>Ambiente</th><th>BTU/TR</th><th>Criticidade</th><th></th></tr></thead><tbody>
              {pmocAtivos.map((a) => <tr key={a.id}><td><strong>{a.ativo_nome}</strong><br /><small>{a.tipo_climatizacao || '-'}</small></td><td>{a.ambiente_nome || '-'}</td><td>{fmtNumero(a.capacidade_btu)} / {a.capacidade_tr || '-'}</td><td>{a.criticidade}</td><td className="acoes"><button className="btn sec sm" onClick={() => setAtivoForm({ ...a, id: String(a.id), plano_id: String(a.plano_id), ativo_id: String(a.ativo_id), ambiente_id: a.ambiente_id ? String(a.ambiente_id) : '' })}>Editar</button></td></tr>)}
            </tbody></table>
          </div>
          <form className="card" onSubmit={salvarAtividade}>
            <h3>Atividade PMOC</h3>
            <SelectPlano value={atividadeForm.plano_id} planos={planos} onChange={(v) => setAtividadeForm({ ...atividadeForm, plano_id: v })} />
            <label>Nome *</label><input className="input" value={atividadeForm.nome || ''} onChange={(e) => setAtividadeForm({ ...atividadeForm, nome: e.target.value })} required />
            <div className="grid cols2">
              <div><label>Periodicidade dias *</label><input className="input" type="number" value={atividadeForm.periodicidade_dias || ''} onChange={(e) => setAtividadeForm({ ...atividadeForm, periodicidade_dias: e.target.value })} required /></div>
              <div><label>Tolerancia dias</label><input className="input" type="number" value={atividadeForm.tolerancia_dias || ''} onChange={(e) => setAtividadeForm({ ...atividadeForm, tolerancia_dias: e.target.value })} /></div>
            </div>
            <div className="check-grid">
              <label className="check-line"><input type="checkbox" checked={Boolean(atividadeForm.exige_foto)} onChange={(e) => setAtividadeForm({ ...atividadeForm, exige_foto: e.target.checked })} /> Exige foto</label>
              <label className="check-line"><input type="checkbox" checked={Boolean(atividadeForm.exige_observacao)} onChange={(e) => setAtividadeForm({ ...atividadeForm, exige_observacao: e.target.checked })} /> Exige observação</label>
              <label className="check-line"><input type="checkbox" checked={Boolean(atividadeForm.exige_medicao)} onChange={(e) => setAtividadeForm({ ...atividadeForm, exige_medicao: e.target.checked })} /> Exige medição</label>
            </div>
            <div className="form-actions"><button className="btn" disabled={ocupado}>Salvar atividade</button></div>
          </form>
          <ListaSimples titulo="Atividades" rows={atividades.map((a) => [a.nome, `${a.periodicidade_dias} dias`, a.exige_medicao ? 'Medição' : '-'])} />
        </div>
      )}

      {aba === 'cronograma' && (
        <div className="grid cols2">
          <form className="card" onSubmit={salvarProgramacao}>
            <h3>Programação</h3>
            <SelectPlano value={programacaoForm.plano_id} planos={planos} onChange={(v) => setProgramacaoForm({ ...programacaoForm, plano_id: v, pmoc_ativo_id: '', atividade_id: '' })} />
            <label>Equipamento PMOC *</label><select value={programacaoForm.pmoc_ativo_id || ''} onChange={(e) => setProgramacaoForm({ ...programacaoForm, pmoc_ativo_id: e.target.value })} required><option value="">Selecione</option>{pmocAtivosDoPlano.map((a) => <option key={a.id} value={a.id}>{a.ativo_nome}</option>)}</select>
            <label>Atividade *</label><select value={programacaoForm.atividade_id || ''} onChange={(e) => setProgramacaoForm({ ...programacaoForm, atividade_id: e.target.value })} required><option value="">Selecione</option>{atividadesDoPlano.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}</select>
            <div className="grid cols2">
              <div><label>Início *</label><input className="input" type="date" value={programacaoForm.data_inicio || ''} onChange={(e) => setProgramacaoForm({ ...programacaoForm, data_inicio: e.target.value })} required /></div>
              <div><label>Próxima execução *</label><input className="input" type="date" value={programacaoForm.proxima_execucao || ''} onChange={(e) => setProgramacaoForm({ ...programacaoForm, proxima_execucao: e.target.value })} required /></div>
            </div>
            <div className="form-actions"><button className="btn" disabled={ocupado}>Salvar programação</button></div>
          </form>
          <div className="card">
            <h3>Cronograma PMOC</h3>
            <TabelaProgramacoes rows={programacoes} onGerar={gerarOS} ocupado={ocupado} />
          </div>
        </div>
      )}

      {aba === 'documentos' && (
        <div className="grid cols2">
          <form className="card" onSubmit={salvarDocumento}>
            <h3>Documento técnico</h3>
            <SelectPlano value={documentoForm.plano_id} planos={planos} onChange={(v) => setDocumentoForm({ ...documentoForm, plano_id: v })} />
            <label>Tipo</label><select value={documentoForm.tipo || 'art_trt'} onChange={(e) => setDocumentoForm({ ...documentoForm, tipo: e.target.value })}><option value="art_trt">ART/TRT</option><option value="laudo">Laudo</option><option value="certificado">Certificado</option><option value="projeto">Projeto</option><option value="manual">Manual</option><option value="outro">Outro</option></select>
            <label>Título *</label><input className="input" value={documentoForm.titulo || ''} onChange={(e) => setDocumentoForm({ ...documentoForm, titulo: e.target.value })} required />
            <div className="grid cols2">
              <div><label>Emissão</label><input className="input" type="date" value={documentoForm.data_emissao || ''} onChange={(e) => setDocumentoForm({ ...documentoForm, data_emissao: e.target.value })} /></div>
              <div><label>Validade</label><input className="input" type="date" value={documentoForm.data_validade || ''} onChange={(e) => setDocumentoForm({ ...documentoForm, data_validade: e.target.value })} /></div>
            </div>
            <label>Arquivo *</label><input className="input" type="file" required onChange={(e) => setDocArquivo(e.target.files?.[0] || null)} />
            <div className="form-actions"><button className="btn" disabled={ocupado}>Salvar documento</button></div>
          </form>
          <ListaSimples titulo="Documentos" rows={documentos.map((d) => [d.titulo, d.tipo, fmtData(d.data_validade)])} />
        </div>
      )}

      {aba === 'mttr' && (
        <div className="grid cols2">
          <form className="card" onSubmit={salvarFalha}>
            <h3>Evento de falha</h3>
            <label>Equipamento *</label><select value={falhaForm.ativo_id || ''} onChange={(e) => setFalhaForm({ ...falhaForm, ativo_id: e.target.value })} required><option value="">Selecione</option>{ativos.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}</select>
            <label>Início da falha *</label><input className="input" type="datetime-local" value={falhaForm.falha_inicio || ''} onChange={(e) => setFalhaForm({ ...falhaForm, falha_inicio: e.target.value })} required />
            <div className="grid cols2">
              <div><label>Início reparo</label><input className="input" type="datetime-local" value={falhaForm.reparo_inicio || ''} onChange={(e) => setFalhaForm({ ...falhaForm, reparo_inicio: e.target.value })} /></div>
              <div><label>Fim reparo</label><input className="input" type="datetime-local" value={falhaForm.reparo_fim || ''} onChange={(e) => setFalhaForm({ ...falhaForm, reparo_fim: e.target.value })} /></div>
            </div>
            <label>Retorno à operação</label><input className="input" type="datetime-local" value={falhaForm.retorno_operacao || ''} onChange={(e) => setFalhaForm({ ...falhaForm, retorno_operacao: e.target.value })} />
            <label>Causa</label><input className="input" value={falhaForm.causa || ''} onChange={(e) => setFalhaForm({ ...falhaForm, causa: e.target.value })} />
            <div className="form-actions"><button className="btn" disabled={ocupado}>Registrar falha</button></div>
          </form>
          <ListaSimples titulo="Falhas registradas" rows={falhas.map((f) => [f.ativo_nome, fmtData(f.falha_inicio), f.mttr_operacional_minutos ? `${f.mttr_operacional_minutos} min` : '-'])} />
        </div>
      )}
    </div>
  );
}

function SelectPlano({ value, planos, onChange }: { value: string; planos: Plano[]; onChange: (v: string) => void }) {
  return (
    <>
      <label>Plano PMOC *</label>
      <select value={value || ''} onChange={(e) => onChange(e.target.value)} required>
        <option value="">Selecione</option>
        {planos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
      </select>
    </>
  );
}

function TabelaProgramacoes({ rows, onGerar, ocupado }: { rows: Programacao[]; onGerar: (id: number) => void; ocupado: boolean }) {
  return (
    <div className="tabela-wrap" style={{ border: 0, boxShadow: 'none' }}>
      <table style={{ minWidth: 0 }}>
        <thead><tr><th>Prevista</th><th>Equipamento</th><th>Atividade</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {rows.map((p) => <tr key={p.id}><td>{fmtData(p.proxima_execucao)}</td><td>{p.ativo_nome || '-'}</td><td>{p.atividade_nome || '-'}</td><td><span className="badge">{p.status_calculado || '-'}</span></td><td className="acoes"><button className="btn sm" disabled={ocupado} onClick={() => onGerar(p.id)}>Gerar OS</button></td></tr>)}
          {!rows.length && <tr><td colSpan={5} className="vazio">Sem programações.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ListaSimples({ titulo, rows }: { titulo: string; rows: Array<Array<string | number | null | undefined>> }) {
  return (
    <div className="card">
      <h3>{titulo}</h3>
      {rows.map((r, i) => <div className="linha-lista" key={i}><strong>{r[0] || '-'}</strong><span style={{ color: 'var(--texto-suave)' }}>{r.slice(1).filter(Boolean).join(' - ')}</span></div>)}
      {!rows.length && <p className="vazio">Sem registros.</p>}
    </div>
  );
}
